/**
 * Solution policy defaults.
 *
 * The canonical source of truth is `src/config/permission-defaults.yaml`,
 * loaded into the config registry at startup by `configLoader.server.ts`.
 *
 * The hardcoded constants below serve as FALLBACK values used when the
 * registry has not yet been initialized (e.g. during early boot or in
 * client-side code that cannot access the server-only config loader).
 *
 * Getter functions (`getSolutionPolicyDefaults`, `getSolutionRoleDefaults*`)
 * check the registry first and fall back to the hardcoded constants.
 */

import { getConfigRegistry } from '@/config/industry-solutions/configLoader';

export const SOLUTION_POLICY_DEFAULTS: Record<string, unknown> = {
  "ai_proposal_approve": false,
  "ai_proposal_generate": false,
  "ai_proposal_view_tab": false,
  "allow_appointment_detours": true,
  "allow_inspection_remind_later": false,
  "allow_multiple_jobs_without_checkout": false,
  "allow_multiple_signatures": false,
  "allow_work_multiple_jobs_simultaneously": false,
  "auto_checkout_jobs_on_clock_out": true,
  "auto_populate_expenses_on_job": false,
  "auto_populate_materials_on_job": false,
  "auto_populate_time_entries_on_job": false,
  "auto_present_jobs_on_clock_in": true,
  "background_tracking": false,
  "background_tracking_end_hour": 17,
  "background_tracking_start_hour": 8,
  "background_tracking_time_attendance": false,
  "can_complete_jobs": true,
  "can_create_jobs": false,
  "can_mark_job_arrived": true,
  "can_mark_job_en_route": true,
  "can_view_inventory": true,
  "client_thread": true,
  "email_auto_create_tasks": false,
  "email_auto_reopen_on_reply": true,
  "email_claim_workflow": true,
  "email_pdf_on_completion": false,
  "email_support_channel": false,
  "enable_barcode_scanning": false,
  "enable_bom_viewer": false,
  "enable_checklist_forms": true,
  "enable_compliance_audits": false,
  "enable_compliance_certifications": false,
  "enable_compliance_hazards": false,
  "enable_compliance_incidents": false,
  "enable_compliance_inspections": false,
  "enable_customer_history": false,
  "enable_daily_compliance_summary": false,
  "enable_document_generation": false,
  "enable_drawing_viewer": false,
  "enable_failed_delivery": false,
  "enable_follow_up_scheduling": false,
  "enable_gap_analysis": false,
  "enable_location_tagging": false,
  "enable_manifest": false,
  "enable_measurements": false,
  "enable_navigation": false,
  "enable_ncr_reporting": false,
  "enable_odometer_capture": false,
  "enable_offline_mode": false,
  "enable_package_scanning": false,
  "enable_parts_lookup": false,
  "enable_photo_capture": true,
  "enable_pod": false,
  "enable_qc_inspection": false,
  "enable_qr_scanning": false,
  "enable_quote_generation": true,
  "enable_report_issue": false,
  "enable_service_reports": false,
  "enable_signature_capture": true,
  "enable_site_assessment": false,
  "enable_smart_clock_integration": false,
  "enable_stage_management": false,
  "enable_time_entry": false,
  "enable_vehicle_change": false,
  "enable_voice_notes": false,
  "enable_waybill_generation": false,
  "enforce_gps_smart_task": false,
  "enforce_route_order": true,
  "finance_batch_approval": false,
  "finance_batch_posting": false,
  "finance_invoicing": false,
  "generate_invoice_on_job_complete": false,
  "generate_pdf_on_completion": false,
  "generate_quote_on_job_complete": false,
  "geofence_enforcement_mode": false,
  "is_field_worker": true,
  "job_threads": true,
  "manufacturing_require_operational_forms_before_stage_complete": false,
  "manufacturing_require_photo_before_stage_complete": false,
  "manufacturing_require_signature_before_stage_complete": false,
  "manufacturing_show_issued_drawings_in_work_breakdown": true,
  "manufacturing_stage_handoff_gates": false,
  "message_centre": true,
  "my_tasks": false,
  "notifications_enabled": true,
  "operations_expense_approval_gate": false,
  "operations_job_billing_finalize": false,
  "operations_job_billing_timers_signoff": false,
  "operations_job_billing_workspace": true,
  "operations_job_edit_field_service_photos": true,
  "operations_job_edit_timers_for_completion": true,
  "operations_job_edit_work_item_comments": true,
  "operations_job_generate_invoice": false,
  "procurement_document_capture": false,
  "require_admin_for_inspection": false,
  "require_daily_pre_trip_inspection": false,
  "require_digital_logbook": false,
  "require_document_before_complete": false,
  "require_eta_for_travel": true,
  "require_fleet_driver_designation": false,
  "require_gps_for_travel": true,
  "require_inspection_ai_anpr": true,
  "require_inspection_on_vehicle_change": false,
  "require_odometer_photo": false,
  "require_parts_before_complete": false,
  "require_photos_after_complete": true,
  "require_photos_before_complete": false,
  "require_signature": true,
  "require_valid_drivers_license": false,
  "require_vehicle_for_travel": false,
  "require_vehicle_proximity": false,
  "schedule_thread": true,
  "scheduled_inspection_interval": false,
  "show_when_online": true,
  "smart_task_appointments": true,
  "smart_task_assessment_recording": false,
  "smart_task_bill_creation": false,
  "smart_task_breakdown_recording": false,
  "smart_task_child_entries": false,
  "smart_task_closed_item_retention": true,
  "smart_task_closed_item_retention_days": 7,
  "smart_task_credit_note_creation": false,
  "smart_task_daily_forms": true,
  "smart_task_display_mode": "appointment_centric",
  "smart_task_expense_creation": false,
  "smart_task_field_worker": true,
  "smart_task_fuel_recording": false,
  "smart_task_gpa_download_group_photos": true,
  "smart_task_gpa_download_job_photos": true,
  "smart_task_gpa_edit_other_appointments": true,
  "smart_task_gpa_view_other_appointments": true,
  "smart_task_gpa_view_parent_job": true,
  "smart_task_grouped_appointments": true,
  "smart_task_invoice_creation": false,
  "smart_task_job_metrics": false,
  "smart_task_maintenance_recording": false,
  "smart_task_mobile_only": false,
  "smart_task_non_driver": false,
  "smart_task_proposal_creation": false,
  "smart_task_purchase_recording": false,
  "smart_task_quote_creation": true,
  "smart_task_route_stop_recording": false,
  "smart_task_routes": true,
  "smart_task_site_visit_recording": false,
  "smart_task_supervision_recording": false,
  "smart_task_template_key": null,
  "smart_task_template_keys": [],
  "smart_task_toll_recording": false,
  "smart_task_training_recording": false,
  "support_desk_access": false,
  "ticket_support_channel": true,
  "time_tracking_enabled": true,
  "total_assistant": false,
  "total_field_action": false,
  "total_field_action_appointment_only": false,
  "total_field_action_receipt_ai_validation": false,
  "total_field_action_require_public_comment": false,
  "total_field_action_required_photo_count": 3,
  "total_field_action_token_ttl_minutes": 20,
  "total_field_action_web_fallback": false,
  "total_field_action_whatsapp": false,
  "video_calls": true,
  "view_all_jobs": false,
  "view_customer_details": true,
  "view_job_financials": true,
  "voice_calls": true,
  "operations_action_permissions": {"item_types": {"job": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "bill": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}, "task": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "asset": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "audit": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "quote": {"close": false, "create": true, "delete": false, "reopen": false, "resolve": false}, "ticket": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "invoice": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}, "project": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true, "admin_reopen": true}, "incident": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "inspection": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "appointment": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "credit_note": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "certification": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}}},
};

/**
 * Per-role default permissions extracted from the pre-migration backup.
 *
 * The old system stored role_defaults in teams.settings JSONB, with different
 * default values for user/supervisor/admin roles. Admin gets more permissions
 * (view_all_jobs, can_create_jobs, view_job_financials, etc.) while user gets
 * a more restricted set.
 */

export const SOLUTION_ROLE_DEFAULTS_USER: Record<string, unknown> = {
  "ai_proposal_approve": false,
  "ai_proposal_generate": false,
  "ai_proposal_view_tab": false,
  "allow_appointment_detours": true,
  "allow_inspection_remind_later": false,
  "allow_multiple_jobs_without_checkout": false,
  "allow_multiple_signatures": false,
  "allow_work_multiple_jobs_simultaneously": false,
  "auto_checkout_jobs_on_clock_out": true,
  "auto_populate_expenses_on_job": false,
  "auto_populate_materials_on_job": false,
  "auto_populate_time_entries_on_job": false,
  "auto_present_jobs_on_clock_in": true,
  "background_tracking": false,
  "background_tracking_end_hour": 17,
  "background_tracking_start_hour": 8,
  "background_tracking_time_attendance": false,
  "can_complete_jobs": true,
  "can_create_jobs": false,
  "can_mark_job_arrived": true,
  "can_mark_job_en_route": true,
  "can_view_inventory": true,
  "client_thread": true,
  "email_auto_create_tasks": false,
  "email_auto_reopen_on_reply": true,
  "email_claim_workflow": true,
  "email_pdf_on_completion": false,
  "email_support_channel": false,
  "enable_barcode_scanning": false,
  "enable_bom_viewer": false,
  "enable_checklist_forms": true,
  "enable_compliance_audits": false,
  "enable_compliance_certifications": false,
  "enable_compliance_hazards": false,
  "enable_compliance_incidents": false,
  "enable_compliance_inspections": false,
  "enable_customer_history": false,
  "enable_daily_compliance_summary": false,
  "enable_document_generation": false,
  "enable_drawing_viewer": false,
  "enable_failed_delivery": false,
  "enable_follow_up_scheduling": false,
  "enable_gap_analysis": false,
  "enable_location_tagging": false,
  "enable_manifest": false,
  "enable_measurements": false,
  "enable_navigation": false,
  "enable_ncr_reporting": false,
  "enable_odometer_capture": false,
  "enable_offline_mode": false,
  "enable_package_scanning": false,
  "enable_parts_lookup": false,
  "enable_photo_capture": true,
  "enable_pod": false,
  "enable_qc_inspection": false,
  "enable_qr_scanning": false,
  "enable_quote_generation": true,
  "enable_report_issue": false,
  "enable_service_reports": false,
  "enable_signature_capture": true,
  "enable_site_assessment": false,
  "enable_smart_clock_integration": false,
  "enable_stage_management": false,
  "enable_time_entry": false,
  "enable_vehicle_change": false,
  "enable_voice_notes": false,
  "enable_waybill_generation": false,
  "enforce_gps_smart_task": false,
  "enforce_route_order": true,
  "finance_batch_approval": false,
  "finance_batch_posting": false,
  "finance_invoicing": false,
  "generate_invoice_on_job_complete": false,
  "generate_pdf_on_completion": false,
  "generate_quote_on_job_complete": false,
  "geofence_enforcement_mode": false,
  "is_field_worker": true,
  "job_threads": true,
  "manufacturing_require_operational_forms_before_stage_complete": false,
  "manufacturing_require_photo_before_stage_complete": false,
  "manufacturing_require_signature_before_stage_complete": false,
  "manufacturing_show_issued_drawings_in_work_breakdown": true,
  "manufacturing_stage_handoff_gates": false,
  "message_centre": true,
  "my_tasks": false,
  "notifications_enabled": true,
  "operations_action_permissions": {"item_types": {"job": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "bill": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}, "task": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "asset": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "audit": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "quote": {"close": false, "create": true, "delete": false, "reopen": false, "resolve": false}, "ticket": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "invoice": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}, "project": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true, "admin_reopen": true}, "incident": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "inspection": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "appointment": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "credit_note": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": true}, "certification": {"close": false, "create": false, "delete": false, "reopen": false, "resolve": false}}},
  "operations_expense_approval_gate": false,
  "operations_job_billing_finalize": false,
  "operations_job_billing_timers_signoff": false,
  "operations_job_billing_workspace": true,
  "operations_job_edit_field_service_photos": true,
  "operations_job_edit_timers_for_completion": true,
  "operations_job_edit_work_item_comments": true,
  "operations_job_generate_invoice": false,
  "procurement_document_capture": false,
  "require_admin_for_inspection": false,
  "require_daily_pre_trip_inspection": false,
  "require_digital_logbook": false,
  "require_document_before_complete": false,
  "require_eta_for_travel": true,
  "require_fleet_driver_designation": false,
  "require_gps_for_travel": true,
  "require_inspection_ai_anpr": true,
  "require_inspection_on_vehicle_change": false,
  "require_odometer_photo": false,
  "require_parts_before_complete": false,
  "require_photos_after_complete": true,
  "require_photos_before_complete": false,
  "require_signature": true,
  "require_valid_drivers_license": false,
  "require_vehicle_for_travel": false,
  "require_vehicle_proximity": false,
  "schedule_thread": true,
  "scheduled_inspection_interval": false,
  "show_when_online": true,
  "smart_task_appointments": true,
  "smart_task_assessment_recording": false,
  "smart_task_bill_creation": false,
  "smart_task_breakdown_recording": false,
  "smart_task_child_entries": false,
  "smart_task_closed_item_retention": true,
  "smart_task_closed_item_retention_days": 7,
  "smart_task_credit_note_creation": false,
  "smart_task_daily_forms": true,
  "smart_task_display_mode": "appointment_centric",
  "smart_task_expense_creation": false,
  "smart_task_field_worker": true,
  "smart_task_fuel_recording": false,
  "smart_task_gpa_download_group_photos": true,
  "smart_task_gpa_download_job_photos": true,
  "smart_task_gpa_edit_other_appointments": true,
  "smart_task_gpa_view_other_appointments": true,
  "smart_task_gpa_view_parent_job": true,
  "smart_task_grouped_appointments": true,
  "smart_task_invoice_creation": false,
  "smart_task_job_metrics": false,
  "smart_task_maintenance_recording": false,
  "smart_task_mobile_only": false,
  "smart_task_non_driver": false,
  "smart_task_proposal_creation": false,
  "smart_task_purchase_recording": false,
  "smart_task_quote_creation": true,
  "smart_task_route_stop_recording": false,
  "smart_task_routes": true,
  "smart_task_site_visit_recording": false,
  "smart_task_supervision_recording": false,
  "smart_task_template_key": null,
  "smart_task_template_keys": [],
  "smart_task_toll_recording": false,
  "smart_task_training_recording": false,
  "support_desk_access": false,
  "ticket_support_channel": true,
  "time_tracking_enabled": true,
  "total_assistant": false,
  "total_field_action": false,
  "total_field_action_appointment_only": false,
  "total_field_action_receipt_ai_validation": false,
  "total_field_action_require_public_comment": false,
  "total_field_action_required_photo_count": 3,
  "total_field_action_token_ttl_minutes": 20,
  "total_field_action_web_fallback": false,
  "total_field_action_whatsapp": false,
  "video_calls": true,
  "view_all_jobs": false,
  "view_customer_details": true,
  "view_job_financials": true,
  "voice_calls": true,
};

export const SOLUTION_ROLE_DEFAULTS_SUPERVISOR: Record<string, unknown> = {
  "ai_proposal_approve": false,
  "ai_proposal_generate": false,
  "ai_proposal_view_tab": false,
  "allow_appointment_detours": false,
  "allow_inspection_remind_later": false,
  "allow_multiple_jobs_without_checkout": false,
  "allow_multiple_signatures": true,
  "allow_work_multiple_jobs_simultaneously": false,
  "auto_checkout_jobs_on_clock_out": false,
  "auto_populate_expenses_on_job": false,
  "auto_populate_materials_on_job": false,
  "auto_populate_time_entries_on_job": true,
  "auto_present_jobs_on_clock_in": false,
  "background_tracking": false,
  "background_tracking_end_hour": 17,
  "background_tracking_start_hour": 8,
  "background_tracking_time_attendance": false,
  "can_complete_jobs": true,
  "can_create_jobs": false,
  "can_mark_job_arrived": true,
  "can_mark_job_en_route": true,
  "can_view_inventory": true,
  "client_thread": true,
  "email_auto_create_tasks": false,
  "email_auto_reopen_on_reply": true,
  "email_claim_workflow": true,
  "email_pdf_on_completion": true,
  "email_support_channel": false,
  "enable_barcode_scanning": true,
  "enable_bom_viewer": false,
  "enable_checklist_forms": false,
  "enable_compliance_audits": false,
  "enable_compliance_certifications": false,
  "enable_compliance_hazards": false,
  "enable_compliance_incidents": false,
  "enable_compliance_inspections": false,
  "enable_customer_history": false,
  "enable_daily_compliance_summary": false,
  "enable_document_generation": false,
  "enable_drawing_viewer": false,
  "enable_failed_delivery": false,
  "enable_follow_up_scheduling": false,
  "enable_gap_analysis": false,
  "enable_location_tagging": false,
  "enable_manifest": false,
  "enable_measurements": false,
  "enable_navigation": false,
  "enable_ncr_reporting": false,
  "enable_odometer_capture": false,
  "enable_offline_mode": false,
  "enable_package_scanning": false,
  "enable_parts_lookup": false,
  "enable_photo_capture": true,
  "enable_pod": false,
  "enable_qc_inspection": false,
  "enable_qr_scanning": true,
  "enable_quote_generation": false,
  "enable_report_issue": false,
  "enable_service_reports": false,
  "enable_signature_capture": true,
  "enable_site_assessment": false,
  "enable_smart_clock_integration": false,
  "enable_stage_management": false,
  "enable_time_entry": true,
  "enable_vehicle_change": false,
  "enable_voice_notes": true,
  "enable_waybill_generation": false,
  "enforce_gps_smart_task": true,
  "enforce_route_order": false,
  "finance_batch_approval": false,
  "finance_batch_posting": false,
  "finance_invoicing": false,
  "generate_invoice_on_job_complete": false,
  "generate_pdf_on_completion": true,
  "generate_quote_on_job_complete": false,
  "geofence_enforcement_mode": false,
  "is_field_worker": true,
  "job_threads": true,
  "manufacturing_require_operational_forms_before_stage_complete": false,
  "manufacturing_require_photo_before_stage_complete": false,
  "manufacturing_require_signature_before_stage_complete": false,
  "manufacturing_show_issued_drawings_in_work_breakdown": true,
  "manufacturing_stage_handoff_gates": false,
  "message_centre": true,
  "my_tasks": true,
  "notifications_enabled": true,
  "operations_action_permissions": {"item_types": {"job": {"close": true, "create": false, "delete": false, "reopen": true, "resolve": true}, "bill": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "task": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "asset": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "audit": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "quote": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "ticket": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "invoice": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "project": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "incident": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "inspection": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "appointment": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "credit_note": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}, "certification": {"close": true, "create": true, "delete": false, "reopen": true, "resolve": true}}},
  "operations_expense_approval_gate": false,
  "operations_job_billing_finalize": false,
  "operations_job_billing_timers_signoff": false,
  "operations_job_billing_workspace": true,
  "operations_job_edit_field_service_photos": true,
  "operations_job_edit_timers_for_completion": true,
  "operations_job_edit_work_item_comments": true,
  "operations_job_generate_invoice": false,
  "procurement_document_capture": false,
  "require_admin_for_inspection": false,
  "require_daily_pre_trip_inspection": false,
  "require_digital_logbook": false,
  "require_document_before_complete": false,
  "require_eta_for_travel": true,
  "require_fleet_driver_designation": false,
  "require_gps_for_travel": true,
  "require_inspection_ai_anpr": true,
  "require_inspection_on_vehicle_change": true,
  "require_odometer_photo": false,
  "require_parts_before_complete": false,
  "require_photos_after_complete": true,
  "require_photos_before_complete": false,
  "require_signature": true,
  "require_valid_drivers_license": false,
  "require_vehicle_for_travel": true,
  "require_vehicle_proximity": true,
  "schedule_thread": true,
  "scheduled_inspection_interval": false,
  "show_when_online": true,
  "smart_task_appointments": true,
  "smart_task_assessment_recording": false,
  "smart_task_bill_creation": true,
  "smart_task_breakdown_recording": true,
  "smart_task_child_entries": true,
  "smart_task_closed_item_retention": true,
  "smart_task_closed_item_retention_days": 7,
  "smart_task_credit_note_creation": true,
  "smart_task_daily_forms": true,
  "smart_task_display_mode": "appointment_centric",
  "smart_task_expense_creation": true,
  "smart_task_field_worker": true,
  "smart_task_fuel_recording": true,
  "smart_task_gpa_download_group_photos": false,
  "smart_task_gpa_download_job_photos": false,
  "smart_task_gpa_edit_other_appointments": false,
  "smart_task_gpa_view_other_appointments": false,
  "smart_task_gpa_view_parent_job": false,
  "smart_task_grouped_appointments": false,
  "smart_task_invoice_creation": true,
  "smart_task_job_metrics": false,
  "smart_task_maintenance_recording": false,
  "smart_task_mobile_only": false,
  "smart_task_non_driver": false,
  "smart_task_proposal_creation": true,
  "smart_task_purchase_recording": true,
  "smart_task_quote_creation": true,
  "smart_task_route_stop_recording": false,
  "smart_task_routes": false,
  "smart_task_site_visit_recording": false,
  "smart_task_supervision_recording": false,
  "smart_task_template_key": "financial-management",
  "smart_task_template_keys": ["financial-management", "service-operations", "asset-inventory-management", "compliance-manager"],
  "smart_task_toll_recording": true,
  "smart_task_training_recording": false,
  "support_desk_access": false,
  "ticket_support_channel": true,
  "time_tracking_enabled": true,
  "total_assistant": true,
  "total_field_action": true,
  "total_field_action_appointment_only": true,
  "total_field_action_receipt_ai_validation": false,
  "total_field_action_require_public_comment": false,
  "total_field_action_required_photo_count": 3,
  "total_field_action_token_ttl_minutes": 20,
  "total_field_action_web_fallback": true,
  "total_field_action_whatsapp": true,
  "video_calls": true,
  "view_all_jobs": true,
  "view_customer_details": true,
  "view_job_financials": false,
  "voice_calls": true,
};

export const SOLUTION_ROLE_DEFAULTS_ADMIN: Record<string, unknown> = {
  "ai_proposal_approve": false,
  "ai_proposal_generate": false,
  "ai_proposal_view_tab": false,
  "allow_appointment_detours": true,
  "allow_inspection_remind_later": true,
  "allow_multiple_jobs_without_checkout": false,
  "allow_multiple_signatures": true,
  "allow_work_multiple_jobs_simultaneously": false,
  "auto_checkout_jobs_on_clock_out": false,
  "auto_populate_expenses_on_job": false,
  "auto_populate_materials_on_job": false,
  "auto_populate_time_entries_on_job": true,
  "auto_present_jobs_on_clock_in": false,
  "background_tracking": false,
  "background_tracking_end_hour": 17,
  "background_tracking_start_hour": 8,
  "background_tracking_time_attendance": false,
  "can_complete_jobs": true,
  "can_create_jobs": false,
  "can_mark_job_arrived": true,
  "can_mark_job_en_route": true,
  "can_view_inventory": true,
  "client_thread": true,
  "email_auto_create_tasks": false,
  "email_auto_reopen_on_reply": true,
  "email_claim_workflow": true,
  "email_pdf_on_completion": true,
  "email_support_channel": false,
  "enable_barcode_scanning": false,
  "enable_bom_viewer": false,
  "enable_checklist_forms": false,
  "enable_compliance_audits": false,
  "enable_compliance_certifications": false,
  "enable_compliance_hazards": false,
  "enable_compliance_incidents": false,
  "enable_compliance_inspections": false,
  "enable_customer_history": false,
  "enable_daily_compliance_summary": false,
  "enable_document_generation": false,
  "enable_drawing_viewer": false,
  "enable_failed_delivery": false,
  "enable_follow_up_scheduling": false,
  "enable_gap_analysis": false,
  "enable_location_tagging": false,
  "enable_manifest": false,
  "enable_measurements": false,
  "enable_navigation": false,
  "enable_ncr_reporting": false,
  "enable_odometer_capture": false,
  "enable_offline_mode": false,
  "enable_package_scanning": false,
  "enable_parts_lookup": false,
  "enable_photo_capture": false,
  "enable_pod": false,
  "enable_qc_inspection": false,
  "enable_qr_scanning": false,
  "enable_quote_generation": false,
  "enable_report_issue": false,
  "enable_service_reports": false,
  "enable_signature_capture": false,
  "enable_site_assessment": false,
  "enable_smart_clock_integration": false,
  "enable_stage_management": false,
  "enable_time_entry": false,
  "enable_vehicle_change": false,
  "enable_voice_notes": false,
  "enable_waybill_generation": false,
  "enforce_gps_smart_task": true,
  "enforce_route_order": false,
  "finance_batch_approval": false,
  "finance_batch_posting": false,
  "finance_invoicing": false,
  "generate_invoice_on_job_complete": false,
  "generate_pdf_on_completion": true,
  "generate_quote_on_job_complete": false,
  "geofence_enforcement_mode": false,
  "is_field_worker": true,
  "job_threads": true,
  "manufacturing_require_operational_forms_before_stage_complete": false,
  "manufacturing_require_photo_before_stage_complete": false,
  "manufacturing_require_signature_before_stage_complete": false,
  "manufacturing_show_issued_drawings_in_work_breakdown": true,
  "manufacturing_stage_handoff_gates": false,
  "message_centre": true,
  "my_tasks": false,
  "notifications_enabled": true,
  "operations_action_permissions": {"item_types": {"job": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "bill": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "task": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "asset": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "audit": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "quote": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "ticket": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "invoice": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "project": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "incident": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "inspection": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "appointment": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "credit_note": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}, "certification": {"close": true, "create": true, "delete": true, "reopen": true, "resolve": true}}},
  "operations_expense_approval_gate": false,
  "operations_job_billing_finalize": false,
  "operations_job_billing_timers_signoff": false,
  "operations_job_billing_workspace": true,
  "operations_job_edit_field_service_photos": true,
  "operations_job_edit_timers_for_completion": true,
  "operations_job_edit_work_item_comments": true,
  "operations_job_generate_invoice": false,
  "procurement_document_capture": true,
  "require_admin_for_inspection": false,
  "require_daily_pre_trip_inspection": true,
  "require_digital_logbook": true,
  "require_document_before_complete": false,
  "require_eta_for_travel": true,
  "require_fleet_driver_designation": true,
  "require_gps_for_travel": true,
  "require_inspection_ai_anpr": true,
  "require_inspection_on_vehicle_change": true,
  "require_odometer_photo": false,
  "require_parts_before_complete": false,
  "require_photos_after_complete": true,
  "require_photos_before_complete": false,
  "require_signature": true,
  "require_valid_drivers_license": false,
  "require_vehicle_for_travel": true,
  "require_vehicle_proximity": true,
  "schedule_thread": true,
  "scheduled_inspection_interval": false,
  "show_when_online": true,
  "smart_task_appointments": true,
  "smart_task_assessment_recording": false,
  "smart_task_bill_creation": true,
  "smart_task_breakdown_recording": true,
  "smart_task_child_entries": true,
  "smart_task_closed_item_retention": true,
  "smart_task_closed_item_retention_days": 3,
  "smart_task_credit_note_creation": true,
  "smart_task_daily_forms": false,
  "smart_task_display_mode": "appointment_centric",
  "smart_task_expense_creation": true,
  "smart_task_field_worker": true,
  "smart_task_fuel_recording": true,
  "smart_task_gpa_download_group_photos": true,
  "smart_task_gpa_download_job_photos": true,
  "smart_task_gpa_edit_other_appointments": true,
  "smart_task_gpa_view_other_appointments": true,
  "smart_task_gpa_view_parent_job": true,
  "smart_task_grouped_appointments": true,
  "smart_task_invoice_creation": true,
  "smart_task_job_metrics": false,
  "smart_task_maintenance_recording": true,
  "smart_task_mobile_only": false,
  "smart_task_non_driver": false,
  "smart_task_proposal_creation": true,
  "smart_task_purchase_recording": true,
  "smart_task_quote_creation": true,
  "smart_task_route_stop_recording": false,
  "smart_task_routes": false,
  "smart_task_site_visit_recording": false,
  "smart_task_supervision_recording": false,
  "smart_task_template_key": "financial-management",
  "smart_task_template_keys": ["financial-management", "service-operations", "asset-inventory-management", "compliance-manager"],
  "smart_task_toll_recording": true,
  "smart_task_training_recording": false,
  "support_desk_access": false,
  "ticket_support_channel": true,
  "time_tracking_enabled": true,
  "total_assistant": false,
  "total_field_action": false,
  "total_field_action_appointment_only": false,
  "total_field_action_receipt_ai_validation": false,
  "total_field_action_require_public_comment": false,
  "total_field_action_required_photo_count": 1,
  "total_field_action_token_ttl_minutes": 20,
  "total_field_action_web_fallback": false,
  "total_field_action_whatsapp": false,
  "video_calls": true,
  "view_all_jobs": true,
  "view_customer_details": true,
  "view_job_financials": true,
  "voice_calls": true,
};

// ============================================================================
// YAML-BACKED GETTERS (preferred over direct constant access)
// ============================================================================
// These functions check the config registry (populated from
// permission-defaults.yaml) first, falling back to the hardcoded
// constants above if the registry is not yet initialized.
// ============================================================================

/**
 * Get the solution policy defaults (feature_defaults + operations_action_permissions).
 * Prefers YAML-loaded values from the config registry; falls back to hardcoded constants.
 */
export function getSolutionPolicyDefaults(): Record<string, unknown> {
  const registry = getConfigRegistry();
  const yamlDefaults = registry.isInitialized() ? registry.getPermissionDefaults() : null;
  if (yamlDefaults) {
    return {
      ...yamlDefaults.feature_defaults,
      operations_action_permissions: yamlDefaults.operations_action_permissions,
    };
  }
  return SOLUTION_POLICY_DEFAULTS;
}

/**
 * Get the user role defaults.
 * Prefers YAML-loaded values; falls back to hardcoded constants.
 */
export function getSolutionRoleDefaultsUser(): Record<string, unknown> {
  const registry = getConfigRegistry();
  const yamlDefaults = registry.isInitialized() ? registry.getPermissionDefaults() : null;
  if (yamlDefaults) {
    return {
      ...yamlDefaults.feature_defaults,
      ...yamlDefaults.role_defaults.user,
      operations_action_permissions: yamlDefaults.role_defaults.user.operations_action_permissions
        ?? yamlDefaults.operations_action_permissions,
    };
  }
  return SOLUTION_ROLE_DEFAULTS_USER;
}

/**
 * Get the supervisor role defaults.
 * Prefers YAML-loaded values; falls back to hardcoded constants.
 */
export function getSolutionRoleDefaultsSupervisor(): Record<string, unknown> {
  const registry = getConfigRegistry();
  const yamlDefaults = registry.isInitialized() ? registry.getPermissionDefaults() : null;
  if (yamlDefaults) {
    return {
      ...yamlDefaults.feature_defaults,
      ...yamlDefaults.role_defaults.supervisor,
      operations_action_permissions: yamlDefaults.role_defaults.supervisor.operations_action_permissions
        ?? yamlDefaults.operations_action_permissions,
    };
  }
  return SOLUTION_ROLE_DEFAULTS_SUPERVISOR;
}

/**
 * Get the admin role defaults.
 * Prefers YAML-loaded values; falls back to hardcoded constants.
 */
export function getSolutionRoleDefaultsAdmin(): Record<string, unknown> {
  const registry = getConfigRegistry();
  const yamlDefaults = registry.isInitialized() ? registry.getPermissionDefaults() : null;
  if (yamlDefaults) {
    return {
      ...yamlDefaults.feature_defaults,
      ...yamlDefaults.role_defaults.admin,
      operations_action_permissions: yamlDefaults.role_defaults.admin.operations_action_permissions
        ?? yamlDefaults.operations_action_permissions,
    };
  }
  return SOLUTION_ROLE_DEFAULTS_ADMIN;
}

